import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Crown, ArrowRight, Loader2, BadgeCheck } from "lucide-react";
import { api } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useI18n, BENEFIT_KEYS, TIER_KEYS } from "@/i18n";
import { Button } from "@/components/ui/button";

export default function Membership() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = async () => {
    setError(false);
    setLoading(true);
    try {
      const p = await api("/membership/plans");
      setPlans(p);
      if (user) {
        const m = await api("/membership/me").catch(() => ({ member: null }));
        setMember(m.member);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [user]);

  const isActive = member?.status === "active";

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-md px-4 py-24 text-center">
        <p className="text-ink-soft">{t("loadError")}</p>
        <Button onClick={load} className="mt-4 rounded-full bg-brand hover:bg-brand-dark">{t("retry")}</Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-12">
      <div className="text-center">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-tint px-4 py-1 text-xs font-bold text-brand">
          <Crown size={14} /> {t("memTitle")}
        </span>
        <h1 className="mt-3 text-3xl font-black text-ink sm:text-4xl">{t("memTitle")}</h1>
        <p className="mx-auto mt-2 max-w-xl text-ink-muted">{t("memSubtitle")}</p>
      </div>

      {isActive && (
        <div className="mx-auto mt-8 flex max-w-xl items-center gap-3 rounded-2xl border border-[#A5D6A7] bg-[#E8F5E9] p-4">
          <BadgeCheck className="text-[#2E7D32]" />
          <div className="flex-1 text-sm">
            <div className="font-bold text-[#2E7D32]">{member.membership_id}</div>
            <div className="text-ink-soft">{member.designation}</div>
          </div>
          <Button onClick={() => navigate("/card")} className="rounded-full bg-[#2E7D32] hover:bg-[#256628]">
            {t("cardHeadTitle")}
          </Button>
        </div>
      )}

      <div className="mt-10 grid gap-6 sm:grid-cols-2">
        {plans.map((plan) => {
          const featured = plan.tier === 101;
          return (
            <div
              key={plan.tier}
              data-testid={`plan-${plan.tier}`}
              className={`relative flex flex-col rounded-3xl border-2 bg-white p-7 transition-all hover:shadow-xl ${featured ? "border-brand" : "border-line"}`}
            >
              {plan.promo && (
                <span className="absolute -top-3 left-6 rounded-full bg-brand px-3 py-1 text-[11px] font-bold text-white shadow">
                  {t("promoTill")}
                </span>
              )}
              {featured && (
                <span className="absolute -top-3 right-6 rounded-full bg-brand-deep px-3 py-1 text-[11px] font-bold text-white shadow">
                  ★
                </span>
              )}
              <div className="text-sm font-bold uppercase tracking-wide text-brand">
                {t(TIER_KEYS[plan.label] || plan.label)}
              </div>
              <div className="mt-2 flex items-end gap-1">
                <span className="text-4xl font-black text-ink">₹{plan.tier}</span>
                <span className="mb-1 text-sm text-ink-muted">/ {t("perYear")}</span>
              </div>
              <ul className="mt-5 flex-1 space-y-3">
                {(plan.benefits || []).map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-ink-soft">
                    <Check size={16} className="mt-0.5 flex-shrink-0 text-brand" />
                    <span>{t(BENEFIT_KEYS[b] || "") || b}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`select-plan-${plan.tier}`}
                onClick={() => navigate(`/register?tier=${plan.tier}`)}
                className={`mt-6 h-12 w-full rounded-full text-base font-bold ${featured ? "bg-brand hover:bg-brand-dark" : "bg-[#29211D] hover:bg-black"}`}
              >
                {isActive ? t("memUpgrade") : t("memContinue")} <ArrowRight size={16} className="ml-1" />
              </Button>
            </div>
          );
        })}
      </div>

      <button
        onClick={() => navigate("/contact")}
        data-testid="contact-hq-card"
        className="mx-auto mt-8 flex w-full max-w-xl items-center gap-3 rounded-2xl border border-line bg-white p-5 text-left hover:border-brand"
      >
        <div className="flex-1 text-sm text-ink-soft">{t("contactHQ")}</div>
        <ArrowRight size={18} className="text-brand" />
      </button>
    </div>
  );
}
